import type { InstrumentType, MidiClip, MidiNote, Project, SnapValue, WamParameterInfo } from '@/domain/types';
import { createInstrument, createMidiClip } from '@/domain/project';
import { insertClips, withClip, withTrack } from '@/domain/mutate';
import { signatureMapOf } from '@/domain/selectors';
import { snapStepTicks } from '@/timing/snap';
import { SNAP_VALUES } from '@/timing/snap';
import {
  INSTRUMENT_DESCRIPTORS,
  defaultInstrumentParams,
  instrumentParamDescriptorFor,
} from '@/engine/instruments/descriptors';
import { isWamUrl } from '@/engine/effects/wamHost';
import { readWamState } from '@/domain/format/parse';
import { newClipId, newNoteId } from '@/util/id';
import { defineCommand } from './registry';
import { boolParam, enumParam, idParam, idsParam, intParam, numberParam, stringParam } from './params';

/**
 * MIDI and instrument editing.
 *
 * Notes live inside their clip rather than in a top-level collection. That
 * keeps a clip self-contained — copying, splitting or deleting one moves its
 * notes with it, with no separate bookkeeping to forget — and it is what lets
 * the piano roll and the drum sequencer be two views of the same data.
 */

const INSTRUMENT_TYPES: readonly InstrumentType[] = ['synth', 'drumMachine', 'sampler', 'wam'];
const MAX_NOTES_PER_CLIP = 20000;

function withMidiClip(project: Project, clipId: string, update: (clip: MidiClip) => MidiClip): Project {
  const clip = project.clips[clipId];
  if (!clip || clip.kind !== 'midi') return project;
  return withClip(project, clipId, (c) => (c.kind === 'midi' ? update(c) : c));
}

function sortNotes(notes: MidiNote[]): MidiNote[] {
  return [...notes].sort((a, b) => a.startTicks - b.startTicks || a.pitch - b.pitch);
}

export const addMidiClip = defineCommand<{
  clipId: string;
  trackId: string;
  name: string;
  startTicks: number;
  durationTicks: number;
}>({
  type: 'clip.addMidi',
  label: () => 'Add MIDI clip',
  parse: (raw) => {
    const clipId = raw.clipId === undefined ? newClipId() : idParam(raw.clipId);
    const trackId = idParam(raw.trackId);
    const startTicks = intParam(raw.startTicks, 0, Number.MAX_SAFE_INTEGER, 0);
    const durationTicks = intParam(raw.durationTicks, 1, Number.MAX_SAFE_INTEGER, 3840);
    if (!clipId || !trackId || startTicks === null || durationTicks === null) return null;
    return { clipId, trackId, name: stringParam(raw.name, 128, 'MIDI') ?? 'MIDI', startTicks, durationTicks };
  },
  apply: (project, p) => {
    const track = project.tracks[p.trackId];
    // MIDI belongs on an instrument track; anywhere else it would be silent.
    if (!track || track.kind !== 'instrument' || project.clips[p.clipId]) return project;
    const clip = createMidiClip({
      trackId: p.trackId,
      name: p.name,
      startTicks: p.startTicks,
      durationTicks: p.durationTicks,
      id: p.clipId,
    });
    return insertClips(project, [clip]);
  },
});

export const addNote = defineCommand<{
  clipId: string;
  noteId: string;
  pitch: number;
  velocity: number;
  startTicks: number;
  durationTicks: number;
  channel: number;
}>({
  type: 'midi.addNote',
  label: () => 'Add note',
  parse: (raw) => {
    const clipId = idParam(raw.clipId);
    const noteId = raw.noteId === undefined ? newNoteId() : idParam(raw.noteId);
    const pitch = intParam(raw.pitch, 0, 127);
    const velocity = intParam(raw.velocity, 1, 127, 100);
    const startTicks = intParam(raw.startTicks, 0, Number.MAX_SAFE_INTEGER);
    const durationTicks = intParam(raw.durationTicks, 1, Number.MAX_SAFE_INTEGER);
    if (!clipId || !noteId || pitch === null || velocity === null || startTicks === null || durationTicks === null) {
      return null;
    }
    return {
      clipId,
      noteId,
      pitch,
      velocity,
      startTicks,
      durationTicks,
      channel: intParam(raw.channel, 0, 15, 0) ?? 0,
    };
  },
  apply: (project, p) =>
    withMidiClip(project, p.clipId, (clip) => {
      if (clip.notes.length >= MAX_NOTES_PER_CLIP) return clip;
      if (clip.notes.some((n) => n.id === p.noteId)) return clip;
      // Drawing over an existing note replaces it rather than stacking a
      // duplicate that would double the level and be impossible to select.
      const overlapping = clip.notes.find(
        (n) => n.pitch === p.pitch && p.startTicks < n.startTicks + n.durationTicks && n.startTicks < p.startTicks + p.durationTicks,
      );
      const kept = overlapping ? clip.notes.filter((n) => n.id !== overlapping.id) : clip.notes;
      const note: MidiNote = {
        id: p.noteId,
        pitch: p.pitch,
        velocity: p.velocity,
        startTicks: p.startTicks,
        durationTicks: p.durationTicks,
        channel: p.channel,
      };
      return { ...clip, notes: sortNotes([...kept, note]) };
    }),
});

export const moveNotes = defineCommand<{ clipId: string; noteIds: string[]; deltaTicks: number; deltaPitch: number }>({
  type: 'midi.moveNotes',
  label: (p) => (p.noteIds.length === 1 ? 'Move note' : `Move ${p.noteIds.length} notes`),
  parse: (raw) => {
    const clipId = idParam(raw.clipId);
    const noteIds = idsParam(raw.noteIds);
    const deltaTicks = intParam(raw.deltaTicks, -Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, 0);
    const deltaPitch = intParam(raw.deltaPitch, -127, 127, 0);
    if (!clipId || !noteIds || noteIds.length === 0 || deltaTicks === null || deltaPitch === null) return null;
    return { clipId, noteIds, deltaTicks, deltaPitch };
  },
  coalesceKey: (p) => `midiMove:${p.clipId}:${p.noteIds.join(',')}`,
  apply: (project, p) =>
    withMidiClip(project, p.clipId, (clip) => {
      const moving = new Set(p.noteIds);
      // Clamp as a group so a chord keeps its shape when it hits an edge.
      let tickDelta = p.deltaTicks;
      let pitchDelta = p.deltaPitch;
      for (const note of clip.notes) {
        if (!moving.has(note.id)) continue;
        tickDelta = Math.max(tickDelta, -note.startTicks);
        pitchDelta = Math.max(pitchDelta, -note.pitch);
        pitchDelta = Math.min(pitchDelta, 127 - note.pitch);
      }

      return {
        ...clip,
        notes: sortNotes(
          clip.notes.map((note) =>
            moving.has(note.id)
              ? { ...note, startTicks: Math.max(0, note.startTicks + tickDelta), pitch: note.pitch + pitchDelta }
              : note,
          ),
        ),
      };
    }),
});

export const resizeNotes = defineCommand<{ clipId: string; noteIds: string[]; durationTicks: number }>({
  type: 'midi.resizeNotes',
  label: () => 'Resize note',
  parse: (raw) => {
    const clipId = idParam(raw.clipId);
    const noteIds = idsParam(raw.noteIds);
    const durationTicks = intParam(raw.durationTicks, 1, Number.MAX_SAFE_INTEGER);
    return clipId && noteIds && noteIds.length > 0 && durationTicks !== null
      ? { clipId, noteIds, durationTicks }
      : null;
  },
  coalesceKey: (p) => `midiResize:${p.clipId}:${p.noteIds.join(',')}`,
  apply: (project, p) =>
    withMidiClip(project, p.clipId, (clip) => {
      const targets = new Set(p.noteIds);
      return {
        ...clip,
        notes: clip.notes.map((note) =>
          targets.has(note.id) ? { ...note, durationTicks: Math.max(1, p.durationTicks) } : note,
        ),
      };
    }),
});

export const deleteNotes = defineCommand<{ clipId: string; noteIds: string[] }>({
  type: 'midi.deleteNotes',
  label: (p) => (p.noteIds.length === 1 ? 'Delete note' : `Delete ${p.noteIds.length} notes`),
  parse: (raw) => {
    const clipId = idParam(raw.clipId);
    const noteIds = idsParam(raw.noteIds);
    return clipId && noteIds && noteIds.length > 0 ? { clipId, noteIds } : null;
  },
  apply: (project, p) =>
    withMidiClip(project, p.clipId, (clip) => {
      const doomed = new Set(p.noteIds);
      const notes = clip.notes.filter((note) => !doomed.has(note.id));
      return notes.length === clip.notes.length ? clip : { ...clip, notes };
    }),
});

export const setNoteVelocity = defineCommand<{ clipId: string; noteIds: string[]; velocity: number }>({
  type: 'midi.setVelocity',
  label: () => 'Set velocity',
  parse: (raw) => {
    const clipId = idParam(raw.clipId);
    const noteIds = idsParam(raw.noteIds);
    const velocity = intParam(raw.velocity, 1, 127);
    return clipId && noteIds && noteIds.length > 0 && velocity !== null ? { clipId, noteIds, velocity } : null;
  },
  coalesceKey: (p) => `midiVel:${p.clipId}:${p.noteIds.join(',')}`,
  apply: (project, p) =>
    withMidiClip(project, p.clipId, (clip) => {
      const targets = new Set(p.noteIds);
      return {
        ...clip,
        notes: clip.notes.map((note) => (targets.has(note.id) ? { ...note, velocity: p.velocity } : note)),
      };
    }),
});

export const quantizeNotes = defineCommand<{
  clipId: string;
  noteIds: string[] | null;
  snap: SnapValue;
  strength: number;
}>({
  type: 'midi.quantize',
  label: (p) => `Quantize to ${p.snap}`,
  parse: (raw) => {
    const clipId = idParam(raw.clipId);
    const snap = enumParam<SnapValue>(raw.snap, SNAP_VALUES);
    const strength = numberParam(raw.strength, 0, 1, 1);
    if (!clipId || !snap || strength === null) return null;
    const noteIds = raw.noteIds === undefined || raw.noteIds === null ? null : idsParam(raw.noteIds);
    return { clipId, noteIds, snap, strength };
  },
  apply: (project, p) => {
    const clip = project.clips[p.clipId];
    if (!clip || clip.kind !== 'midi') return project;

    const sigMap = signatureMapOf(project);
    const targets = p.noteIds ? new Set(p.noteIds) : null;

    return withMidiClip(project, p.clipId, (midi) => ({
      ...midi,
      notes: sortNotes(
        midi.notes.map((note) => {
          if (targets && !targets.has(note.id)) return note;
          // The grid is measured from the clip's own start, so quantising a
          // clip that does not begin on a bar line still lands on its own grid.
          const absolute = midi.startTicks + note.startTicks;
          const step = snapStepTicks(sigMap, p.snap, absolute);
          if (step <= 0) return note;
          const quantised = Math.round(absolute / step) * step;
          // Partial strength moves a note towards the grid without flattening
          // the performance, which is what makes quantising musical.
          const moved = absolute + (quantised - absolute) * p.strength;
          return { ...note, startTicks: Math.max(0, Math.round(moved - midi.startTicks)) };
        }),
      ),
    }));
  },
});

export const setClipLoop = defineCommand<{ clipId: string; loop: boolean; loopLengthTicks: number | null }>({
  type: 'midi.setClipLoop',
  label: (p) => (p.loop ? 'Loop clip' : 'Stop looping clip'),
  parse: (raw) => {
    const clipId = idParam(raw.clipId);
    const loop = boolParam(raw.loop);
    if (!clipId || loop === null) return null;
    const loopLengthTicks =
      raw.loopLengthTicks === undefined ? null : intParam(raw.loopLengthTicks, 1, Number.MAX_SAFE_INTEGER);
    return { clipId, loop, loopLengthTicks };
  },
  apply: (project, p) =>
    withMidiClip(project, p.clipId, (clip) => ({
      ...clip,
      loop: p.loop,
      loopLengthTicks: p.loopLengthTicks ?? clip.loopLengthTicks,
    })),
});

// ---------------------------------------------------------------------------
// Instruments
// ---------------------------------------------------------------------------

export const setInstrument = defineCommand<{ trackId: string; instrumentType: InstrumentType; wamUrl: string | null }>({
  type: 'instrument.set',
  label: (p) => `Load ${INSTRUMENT_DESCRIPTORS[p.instrumentType].label.toLowerCase()}`,
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const instrumentType = enumParam(raw.instrumentType, INSTRUMENT_TYPES);
    if (!trackId || !instrumentType) return null;
    // A plugin instrument is its URL; without one there is nothing to load.
    const wamUrl = typeof raw.wamUrl === 'string' && isWamUrl(raw.wamUrl) ? raw.wamUrl : null;
    if (instrumentType === 'wam' && !wamUrl) return null;
    return { trackId, instrumentType, wamUrl: instrumentType === 'wam' ? wamUrl : null };
  },
  apply: (project, p) =>
    withTrack(project, p.trackId, (track) => {
      if (track.kind !== 'instrument') return track;
      if (track.instrument?.type === p.instrumentType && (p.wamUrl === null || track.instrument.data.wamUrl === p.wamUrl)) return track;
      const instrument = createInstrument(p.instrumentType, defaultInstrumentParams(p.instrumentType));
      if (p.wamUrl) instrument.data = { wamUrl: p.wamUrl, wamName: 'Web Audio Module', wamParameters: [] };
      return { ...track, instrument };
    }),
});

export const setInstrumentParam = defineCommand<{ trackId: string; paramId: string; value: number }>({
  type: 'instrument.setParam',
  label: () => 'Adjust instrument',
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const paramId = stringParam(raw.paramId, 64);
    const value = numberParam(raw.value, -1e6, 1e6);
    return trackId && paramId && value !== null ? { trackId, paramId, value } : null;
  },
  coalesceKey: (p) => `instParam:${p.trackId}:${p.paramId}`,
  apply: (project, p) =>
    withTrack(project, p.trackId, (track) => {
      const instrument = track.instrument;
      if (!instrument) return track;
      // Clamping against the instrument's own descriptor means a value from a
      // script or an older file can never drive a node out of range.
      const descriptor = instrumentParamDescriptorFor(instrument, p.paramId);
      if (!descriptor) return track;
      const value = Math.min(descriptor.max, Math.max(descriptor.min, p.value));
      if (instrument.params[p.paramId] === value) return track;
      return { ...track, instrument: { ...instrument, params: { ...instrument.params, [p.paramId]: value } } };
    }),
});

/**
 * What a hosted plugin instrument said about itself once it loaded: its name
 * and its parameters, kept in the instrument's state so the project can draw
 * its controls without it. New parameters start at their defaults; values
 * already set are kept.
 */
export const setInstrumentWamInfo = defineCommand<{ trackId: string; name: string; parameters: WamParameterInfo[] }>({
  type: 'instrument.setWamInfo',
  label: () => 'Plugin instrument loaded',
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const name = stringParam(raw.name, 128);
    if (!trackId || !name || !Array.isArray(raw.parameters)) return null;
    const parameters: WamParameterInfo[] = [];
    for (const entry of raw.parameters.slice(0, 256)) {
      if (typeof entry !== 'object' || entry === null) return null;
      const e = entry as Record<string, unknown>;
      const id = stringParam(e.id, 64);
      const min = numberParam(e.min, -1e9, 1e9);
      const max = numberParam(e.max, -1e9, 1e9);
      if (!id || min === null || max === null || !(max > min)) return null;
      parameters.push({
        id,
        label: stringParam(e.label, 64) ?? id,
        min,
        max,
        default: numberParam(e.default, min, max, min) ?? min,
        units: stringParam(e.units, 16) ?? '',
      });
    }
    return { trackId, name, parameters };
  },
  apply: (project, p) =>
    withTrack(project, p.trackId, (track) => {
      const instrument = track.instrument;
      if (!instrument || instrument.type !== 'wam') return track;
      const same =
        instrument.data.wamName === p.name && JSON.stringify(instrument.data.wamParameters ?? []) === JSON.stringify(p.parameters);
      if (same) return track;
      const params = { ...instrument.params };
      for (const parameter of p.parameters) if (params[parameter.id] === undefined) params[parameter.id] = parameter.default;
      return {
        ...track,
        instrument: { ...instrument, params, data: { ...instrument.data, wamName: p.name, wamParameters: p.parameters } },
      };
    }),
});

/** The plugin instrument's whole state, as it reported it; see `effect.setWamState`. */
export const setInstrumentWamState = defineCommand<{ trackId: string; state: unknown }>({
  type: 'instrument.setWamState',
  label: () => 'Plugin instrument state',
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    if (!trackId) return null;
    const state = readWamState(raw.state);
    if (state === undefined) return null;
    return { trackId, state };
  },
  apply: (project, p) =>
    withTrack(project, p.trackId, (track) => {
      const instrument = track.instrument;
      if (!instrument || instrument.type !== 'wam') return track;
      if (JSON.stringify(instrument.data.wamState ?? null) === JSON.stringify(p.state)) return track;
      return { ...track, instrument: { ...instrument, data: { ...instrument.data, wamState: p.state } } };
    }),
});
